import { useState } from "react";
import useUserPageParams from "../../../../../hooks/useUserPageParams";
import { MagnifyingGlassIcon, EnvelopeIcon } from "@heroicons/react/20/solid";
import {
    Alert,
    Center,
    Divider,
    Group,
    LoadingOverlay,
    Pagination,
    ScrollArea,
    Select,
    Stack,
    Text,
    TextInput,
    ThemeIcon,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import type { EmailsResponse, AssociatedObject } from "../../../../../shared/emails-meetings/types";
import { getContactEmails } from "../../../../../services/associatedObjectsEndpoint";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import EmailDetailsDrawer from "../../../../../shared/emails-meetings/EmailDetailsDrawer";
import EmailListItem from "../../../../../shared/emails-meetings/EmailListItem";

const UserEmails = () => {
    const { userId }: any = useUserPageParams();
    const workspaceId = useWorkspaceStore((state) => state.workspace?.id);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 500);

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedEmail, setSelectedEmail] = useState<AssociatedObject | null>(null);

    // Pagination and Sorting State
    const [activePage, setActivePage] = useState(1);
    const [sortBy, setSortBy] = useState<string | null>("created_at");
    const [sortOrder, setSortOrder] = useState<string | null>("desc");
    const pageSize = 50;

    const emailsQuery = useQuery<EmailsResponse, Error>({
        queryKey: ["contact-emails", workspaceId, userId, activePage, sortBy, sortOrder, debouncedSearchQuery],
        queryFn: () =>
            getContactEmails({
                workspace_id: workspaceId as string,
                contact_external_id: userId as string,
                limit: pageSize,
                offset: (activePage - 1) * pageSize,
                sort_by: sortBy || undefined,
                order_by: sortOrder || undefined,
                q: debouncedSearchQuery || undefined,
            }).then((res) => res.data),
        enabled: !!workspaceId && !!userId,
    });

    const emails = emailsQuery.data?.emails ?? [];
    const totalCount = emailsQuery.data?.total_count ?? 0;
    const totalPages = Math.ceil(totalCount / pageSize);

    const handleOpenEmail = (email: AssociatedObject) => {
        setSelectedEmail(email);
        setDetailsOpen(true);
    };

    const handleSortChange = (value: string | null) => {
        if (value === "newest") {
            setSortBy("created_at");
            setSortOrder("desc");
        } else if (value === "oldest") {
            setSortBy("created_at");
            setSortOrder("asc");
        }
    };

    return (
        <div className="h-full bg-white flex flex-col overflow-hidden">
            <LoadingOverlay
                loaderProps={{ size: "sm" }}
                c="main.5"
                visible={emailsQuery.isLoading}
                overlayProps={{ blur: 2 }}
            />

            <Stack gap={10} className="px-4 pt-4 pb-3">
                <Group justify="space-between" align="flex-start">
                    <Stack gap={2}>
                        <Text fw={700} c="text.9" size="lg">Emails</Text>
                        <Text size="xs" c="text.5">
                            {searchQuery ? `${emailsQuery.isLoading ? "..." : totalCount}` : `${totalCount}`} emails
                        </Text>
                    </Stack>
                    <Select
                        size="xs"
                        placeholder="Sort by"
                        data={[
                            { value: "newest", label: "Date (Newest)" },
                            { value: "oldest", label: "Date (Oldest)" },
                        ]}
                        value={sortOrder === "asc" ? "oldest" : "newest"}
                        onChange={handleSortChange}
                        allowDeselect={false}
                        w={140}
                    />
                </Group>

                <TextInput
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.currentTarget.value);
                        setActivePage(1); // Reset to page 1 for new search
                    }}
                    placeholder="Search subject, sender, recipients..."
                    leftSection={<MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />}
                    size="sm"
                    radius="md"
                />
            </Stack>

            <Divider />

            {/* Content */}
            <ScrollArea className="flex-1" type="scroll" scrollHideDelay={500}>
                <Stack gap={0}>
                    {emailsQuery.error && (
                        <div className="p-4">
                            <Alert color="red" variant="light" radius="md">
                                Failed to load emails. Please try again.
                            </Alert>
                        </div>
                    )}

                    {!emailsQuery.isLoading && !emailsQuery.error && emails.length === 0 && (
                        <Center className="py-16">
                            <Stack gap={6} align="center" className="px-4">
                                <ThemeIcon variant="light" color="main" size={46} radius="xl">
                                    <EnvelopeIcon className="h-5 w-5" />
                                </ThemeIcon>
                                <Text fw={700} c="text.9">No emails found</Text>
                                <Text size="sm" c="text.5" ta="center">
                                    {searchQuery ? "Try adjusting your search query." : "No emails associated with this contact yet."}
                                </Text>
                            </Stack>
                        </Center>
                    )}

                    <div className="divide-y divide-gray-100">
                        {emails.map((email) => (
                            <EmailListItem key={email.id} email={email} onOpen={handleOpenEmail} />
                        ))}
                    </div>
                </Stack>
            </ScrollArea>

            {/* Pagination Footer */}
            {totalPages > 1 && (
                <div className="p-3 border-t border-gray-100 bg-gray-50">
                    <Group justify="center">
                        <Pagination
                            total={totalPages}
                            value={activePage}
                            onChange={setActivePage}
                            size="sm"
                            color="main"
                            siblings={1}
                        />
                    </Group>
                </div>
            )}

            <EmailDetailsDrawer
                opened={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                email={selectedEmail}
            />
        </div>
    );
};

export default UserEmails;
